import {
  BulkActionOverlay,
  GridCellText,
  IconButton,
  Input,
  Table,
  type TableColumn,
  Tooltip,
} from '@civilgrid/ui/components';
import type { RowSelectionState } from '@tanstack/react-table';
import type Dinero from 'dinero.js';
import { useState } from 'react';
import { useSnapshot } from 'valtio';
import type { TitleJson } from '~/common/types';
import { useBulkDocumentDownloadEnabled } from '~/hooks/useBulkDocumentDownloadEnabled';
import { useBulkActions } from '~/pages/projectDetails/landRights/bulkActions/useBulkActions';
import { parcelDataCost } from '~/pages/projectDetails/landRights/constant';
import {
  docRowKey,
  formatParties,
  getDocRowLabel,
  getSelectedDocParams,
} from '~/pages/projectDetails/landRights/helper';
import store from '~/pages/projectDetails/landRights/store';
import { DocType, docTypeMap, OrderStep } from '~/pages/projectDetails/landRights/types';

interface ConfirmTextProps {
  docType: DocType;
  selectedParcelAPNs: string[];
  cost: Dinero.Dinero;
}

type DocParams = { DocId: string; CmtId: string };

interface ContentProps {
  docType: DocType;
  titleJson?: string;
  /** Opens the single-document order confirmation, which the parent renders outside this dialog. */
  onOrderDocument?: (docParams: DocParams) => void;
  /** Opens the bulk order confirmation, which the parent renders outside this dialog. */
  onOrderDocuments?: (docsParams: DocParams[]) => void;
}

export const Content = ({ docType, titleJson, onOrderDocument, onOrderDocuments }: ContentProps) => {
  const {
    selectedIds,
    notAvailableParcels,
    parcelsAvailableToDownload,
    vendorFailedParcels,
    failedParcels,
    orderedParcelCount,
    documentOrderStep,
  } = useSnapshot(store) as typeof store;
  const isBulkDownloadEnabled = useBulkDocumentDownloadEnabled();
  const { bulkActions } = useBulkActions();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const getParcelIdsFromSelectedIds = (selectedIds: Set<string>) => {
    return Array.from(selectedIds).map((s) => s.split('.')[1]);
  };

  const textWithTooltip = (text: string) => {
    return (
      <div className="min-w-0 w-full">
        <Tooltip text={text}>
          <GridCellText className="block">{text}</GridCellText>
        </Tooltip>
      </div>
    );
  };

  switch (documentOrderStep) {
    case OrderStep.Confirm: {
      const getConfirmText = ({ docType, selectedParcelAPNs, cost }: ConfirmTextProps): JSX.Element => {
        return (
          <div className="text-sm font-noto">
            Would you like to order {selectedParcelAPNs.length === 1 && 'a '} {docTypeMap[docType]}
            {selectedParcelAPNs.length > 1 && 's'} for parcel
            {selectedParcelAPNs.length > 1 && 's'} <b>{selectedParcelAPNs.slice(0, -1).join(', ')}</b>
            {selectedParcelAPNs.length > 1 ? ' and ' : ' '}
            <b>{selectedParcelAPNs.slice(-1)}</b>
            {docType === DocType.PrelimTitleReport ? (
              <>
                ? <br />
                <br />A CivilGrid representative will contact you to confirm pricing before we place your order.
              </>
            ) : (
              <>
                {' '}
                at a cost of <b>{cost.toFormat()}</b> per report? A total of{' '}
                <b>{cost.multiply(selectedParcelAPNs.length).toFormat()}</b> will be charged to your account.
              </>
            )}
          </div>
        );
      };
      return getConfirmText({
        docType,
        selectedParcelAPNs: getParcelIdsFromSelectedIds(selectedIds),
        cost: parcelDataCost[docType],
      });
    }
    case OrderStep.Processing:
      return <>CivilGrid is preparing your files. This might take a few minutes.</>;
    case OrderStep.Complete: {
      if (notAvailableParcels.length > 0 && notAvailableParcels.length === orderedParcelCount) {
        return (
          <>
            Reports could not be found for the following parcels: <b>{notAvailableParcels.join(', ')}</b>
          </>
        );
      }
      if (notAvailableParcels.length + vendorFailedParcels.length + failedParcels.length > 0) {
        return (
          <div className="space-y-4">
            {parcelsAvailableToDownload.length > 0 && (
              <p>
                Processing complete! {parcelsAvailableToDownload.length} of {orderedParcelCount} reports are
                downloading.
              </p>
            )}
            {notAvailableParcels.length > 0 && (
              <p>
                Reports could not be found for the following parcels: <b>{notAvailableParcels.join(', ')}</b>
              </p>
            )}
            {vendorFailedParcels.length > 0 && (
              <p>
                The title vendor could not produce reports for the following parcels:{' '}
                <b>{vendorFailedParcels.join(', ')}</b>
              </p>
            )}
            {failedParcels.length > 0 && (
              <p>
                Reports could not be processed for the following parcels: <b>{failedParcels.join(', ')}</b>. Please
                contact Support.
              </p>
            )}
          </div>
        );
      }
      return <>Processing complete! Your downloads should begin momentarily.</>;
    }
    case OrderStep.OverTrial:
      return <>You have reached the limit of downloads for your plan. Please contact Support.</>;
    case OrderStep.Error:
      return <>There was an error processing reports for the selected parcels.</>;
    case OrderStep.VendorError:
      return <>Title vendor could not produce these reports.</>;
    case OrderStep.View: {
      let status_ = 'loaded';
      let dataJson: TitleJson[] = [];

      if (!titleJson) {
        store.updateDocumentOrderStep(OrderStep.Error);
        return null;
      }

      try {
        dataJson = JSON.parse(titleJson);
        dataJson = dataJson.filter((n) => n.Date);
      } catch {
        status_ = 'no data';
      }
      const selectedDocParams = getSelectedDocParams(rowSelection, dataJson);
      const showBulkDownload = isBulkDownloadEnabled && docType === DocType.ChainOfTitleReport;

      const columns: TableColumn<TitleJson>[] = [
        {
          id: 'date',
          header: 'Date',
          accessorKey: 'Date',
          size: 110,
          cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
        },
        {
          id: 'type',
          header: 'Type',
          accessorKey: 'DocType',
          size: 140,
          cell: ({ getValue }) => {
            const type = getValue<string>();
            return textWithTooltip(type);
          },
        },
        {
          id: 'parties',
          header: 'Parties',
          accessorFn: (row) => formatParties(row.Party1, row.Party2),
          size: 260,
          cell: ({ getValue }) => {
            const parties = getValue<string>();
            return textWithTooltip(parties);
          },
        },
        {
          id: 'getDocument',
          header: 'Get Document',
          enableGlobalFilter: false,
          cell: ({ row }) => {
            const { DocId, CmtId } = row.original;
            return (
              <IconButton
                icon="download"
                variant="hollow"
                aria-label={`Download document ${DocId}`}
                aria-haspopup="dialog"
                data-testid={`land-rights-download-doc-${DocId}`}
                onClick={() => onOrderDocument?.({ DocId, CmtId })}
              />
            );
          },
        },
      ];

      return status_ === 'loaded' ? (
        <div className="-mt-2 flex min-h-0 flex-1 flex-col">
          {showBulkDownload && (
            <div className="flex mt-0 justify-between items-center mb-4 gap-4">
              <Input
                data-testid="land-rights-doc-search"
                placeholder="Search documents"
                className="max-w-xs"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
          )}
          <div className="relative flex min-h-24 flex-1 flex-col">
            <Table
              aria-label="Chain of title documents"
              data={dataJson}
              columns={columns}
              getRowId={docRowKey}
              getRowLabel={getDocRowLabel}
              enableRowSelection={showBulkDownload}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              enableHiding={false}
              globalFilter={globalFilter}
              onGlobalFilterChange={setGlobalFilter}
              pagination={{ pageIndex: 0, pageSize: dataJson.length || 1 }}
              onPaginationChange={() => {}}
              enableColumnFilters={false}
            />
            {showBulkDownload && Boolean(selectedDocParams.length) && (
              <BulkActionOverlay
                actions={bulkActions.map((action) => ({
                  ...action,
                  onClick: () => onOrderDocuments?.(selectedDocParams),
                }))}
                selection={{ mode: 'partial', selectedIds: Object.keys(rowSelection) }}
                onClose={() => setRowSelection({})}
              />
            )}
          </div>
        </div>
      ) : (
        <div>An error has occurred. Please contact Support.</div>
      );
    }
    default:
      return null;
  }
};
